/* eslint-disable react/prop-types */
import { FC, useEffect, useRef, useState } from "react";
import {
	ChevronUp,
	ChevronDown,
	Trash2,
	Plus,
	Type,
	ImagePlus,
	Video,
	Upload,
	GripVertical,
	Loader2
} from "lucide-react";
import RichTextEditor from "../RichTextEditor/RichTextEditor";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";
import { useAzureVideoDownload } from "src/components/AssetUploaders/useAzureVideoDownload";
import { useAzureFileUpload } from "src/components/AssetUploaders/useAzureFileUpload";
import VideoUploadProgress from "src/components/AssetUploaders/VideoUploadProgress";
import VideoProcessingProgress from "src/components/AssetUploaders/VideoProcessingProgress";
import { useVideoEncodingProgress } from "src/components/AssetUploaders/useVideoEncodingProgress";

export interface ContentBlock {
	id: string;
	type: "text" | "image" | "video";
	content: string;
	/**
	 * Optional author-supplied accessible description for image / video
	 * blocks. Empty string = decorative. When undefined, renderers fall
	 * back to a generic label. The editor UI doesn't yet expose a field
	 * for this — follow-up work — but the data path is in place so the
	 * preview and body HTML can use it when authors do supply it.
	 */
	alt?: string;
	file?: File | null;
	previewUrl?: string | null;
	/**
	 * Local-only Bitmovin processing state for video blocks. Populated by
	 * VideoBlock once the encoding poll resolves. Used by AnnouncementForm
	 * to gate Send / Schedule until every video is ReadyForPlayback — the
	 * backend enforces the same gate, but surfacing it client-side lets us
	 * disable the button and explain why instead of failing on submit.
	 *
	 * Never persisted (serialiseBlocksForPersistence drops it). Reset to
	 * undefined when reopening an announcement so the polling re-confirms.
	 */
	processingStatus?: "Processing" | "ReadyForPlayback";
}

interface Props {
	blocks: ContentBlock[];
	onChange: (blocks: ContentBlock[]) => void;
}

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];
// Match the backend's accepted MIME types (UploadEndpoints.AllowedVideoMimeTypes).
// Webm was historically allowed client-side but the backend rejects it.
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime"];
const MAX_IMAGE_SIZE = 10_485_760; // 10 MB
// Client-side cap matches the backend ChunkedUploadService ceiling (50 GB).
// Bitmovin's documented input size limit is also 50 GB, so going higher
// would silently fail at encoding.
const MAX_VIDEO_SIZE = 50 * 1024 * 1024 * 1024; // 50 GB

const formatBytes = (bytes: number): string => {
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	if (bytes < 1024 * 1024 * 1024)
		return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

let blockCounter = 0;
const generateId = () => `block-${++blockCounter}-${Date.now()}`;

const ImageBlock: FC<{
	block: ContentBlock;
	onUpdate: (block: ContentBlock) => void;
}> = ({ block, onUpdate }) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Two display paths:
	//  - `previewUrl` is the blob URL from just-picked file — used for
	//    instant visual feedback while the upload is in flight.
	//  - `content` is the persisted reference (set after upload success
	//    or loaded from the server) — rendered via <AzureImage> which
	//    resolves it to a SAS URL.
	const hasPreview = Boolean(block.previewUrl);
	const hasPersistedRef = Boolean(block.content) && !block.file;

	// The upload success callback below can resolve several hundred
	// milliseconds (or more, for large files) after handleFile fires.
	// By then the captured `block` is stale — handleFile has already
	// pushed `{file, previewUrl, content:""}` into parent state, and
	// spreading the original would clobber those fields back to their
	// pre-upload values. Reading through this ref at callback time
	// gets the latest block props without forcing the whole callback
	// to be recreated every render.
	const blockRef = useRef(block);
	useEffect(() => {
		blockRef.current = block;
	}, [block]);

	// `onUpdate` is re-created by the parent every render (arrow fn),
	// so we can't list it in effect deps without firing the effect on
	// every render. Route it through a ref too.
	const onUpdateRef = useRef(onUpdate);
	useEffect(() => {
		onUpdateRef.current = onUpdate;
	}, [onUpdate]);

	const {
		upload,
		uploading,
		error: uploadError
	} = useAzureFileUpload((data) => {
		const uploadedId =
			typeof data === "string"
				? data
				: Array.isArray(data) && typeof data[0] === "string"
					? data[0]
					: null;

		if (uploadedId) {
			onUpdateRef.current({
				...blockRef.current,
				file: null,
				content: uploadedId
			});
		}
	});

	useEffect(() => {
		setError(uploadError ? "Failed to upload image, try again." : null);
		if (uploadError && blockRef.current.file) {
			onUpdateRef.current({ ...blockRef.current, file: null });
		}
	}, [uploadError]);

	useEffect(() => {
		const url = block.previewUrl;
		if (url) {
			return () => URL.revokeObjectURL(url);
		}
	}, [block.previewUrl]);

	const handleFile = (file: File) => {
		if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
			setError("Only JPG and PNG files are allowed.");
			return;
		}
		if (file.size > MAX_IMAGE_SIZE) {
			setError("Image must be under 10 MB.");
			return;
		}
		setError(null);
		const url = URL.createObjectURL(file);
		onUpdate({ ...block, file, previewUrl: url, content: "" });
		upload(file, "images", undefined, "admin-content-image");
	};

	return (
		<div>
			<input
				ref={fileInputRef}
				type="file"
				accept="image/jpeg,image/jpg,image/png"
				className="hidden"
				onChange={(e) => {
					const file = e.target.files?.[0];
					if (file) handleFile(file);
				}}
			/>
			{hasPreview || hasPersistedRef ? (
				<>
					<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[400px] relative">
						{hasPreview ? (
							<img
								src={block.previewUrl!}
								alt="Content image"
								className="object-contain w-full h-auto max-h-[400px]"
							/>
						) : (
							<AzureImage
								uuid={block.content}
								imageProfile="admin-content-image"
								alt="Content image"
								className="object-contain w-full h-auto max-h-[400px]"
							/>
						)}
						{uploading && (
							<div
								role="status"
								aria-live="polite"
								className="absolute inset-0 flex items-center justify-center bg-black/30 text-white text-sm font-medium"
							>
								<Loader2 className="h-4 w-4 animate-spin mr-2" /> Uploading…
							</div>
						)}
					</div>
					<div className="flex items-center gap-2">
						<button
							type="button"
							disabled={uploading}
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors disabled:opacity-50 disabled:pointer-events-none"
							onClick={() => fileInputRef.current?.click()}
						>
							Change image
						</button>
						<button
							type="button"
							disabled={uploading}
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-destructive/40 bg-white text-destructive text-sm font-medium cursor-pointer hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors disabled:opacity-50 disabled:pointer-events-none"
							onClick={() =>
								onUpdate({
									...block,
									file: null,
									previewUrl: null,
									content: ""
								})
							}
						>
							Remove
						</button>
					</div>
				</>
			) : (
				<div
					role="button"
					tabIndex={0}
					onClick={() => fileInputRef.current?.click()}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							fileInputRef.current?.click();
						}
					}}
					onDragOver={(e) => {
						e.preventDefault();
						setIsDragging(true);
					}}
					onDragLeave={(e) => {
						e.preventDefault();
						setIsDragging(false);
					}}
					onDrop={(e) => {
						e.preventDefault();
						setIsDragging(false);
						const file = e.dataTransfer.files[0];
						if (file) handleFile(file);
					}}
					className={[
						"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors cursor-pointer",
						isDragging
							? "border-primary bg-primary/10"
							: "border-border bg-white hover:border-primary hover:bg-primary/5"
					].join(" ")}
				>
					<Upload className="h-5 w-5 text-muted-foreground" />
					<div>
						<p className="text-sm font-medium text-foreground">
							Drag image here or click to browse
						</p>
						<p className="text-xs text-muted-foreground mt-1">
							JPG or PNG, under 10 MB
						</p>
					</div>
				</div>
			)}
			{error && (
				<p role="alert" className="text-sm text-destructive mt-2">
					{error}
				</p>
			)}
		</div>
	);
};

/**
 * Thin wrapper around {@link useAzureVideoDownload} so the editor can
 * render a persisted video block's playback URL. The hook polls the
 * backend until Azure Media Services finishes processing; while that
 * happens we show a small "Preparing video…" message.
 */
const PersistedVideoPreview: FC<{
	reference: string;
	url: string | undefined;
	processingStatus: string;
}> = ({ reference, url, processingStatus }) => {
	// Poll Bitmovin's live encoding progress only while the asset is
	// genuinely in the encoding window. `processingStatus === "Processing"`
	// is the explicit "Bitmovin is working" signal from the playback
	// poll; transient non-ready states (URL still negotiating, etc.)
	// shouldn't trigger encoding-progress traffic.
	const encodingState = useVideoEncodingProgress(
		reference,
		!url && processingStatus === "Processing"
	);

	if (url) {
		return (
			<video
				controls
				src={url}
				aria-label="Announcement video"
				className="w-full max-h-[400px]"
			/>
		); // eslint-disable-line
	}
	return <VideoProcessingProgress state={encodingState} variant="inline" />;
};

const VideoBlock: FC<{
	block: ContentBlock;
	onUpdate: (block: ContentBlock) => void;
}> = ({ block, onUpdate }) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const hasPreview = Boolean(block.previewUrl);
	const hasPersistedRef = Boolean(block.content) && !block.file;

	// See ImageBlock for the full rationale — the video upload pipeline
	// takes even longer to resolve than an image, so stale-closure risk
	// is higher here. Read through the ref at callback time.
	const blockRef = useRef(block);
	useEffect(() => {
		blockRef.current = block;
	}, [block]);

	const onUpdateRef = useRef(onUpdate);
	useEffect(() => {
		onUpdateRef.current = onUpdate;
	}, [onUpdate]);

	// Canonical upload pipeline — `useAzureFileUpload` auto-routes
	// `upload(file, "video")` through the chunked transport, surfacing
	// `videoUploadState` (status, bytes, speed, ETA) for the shared
	// progress UI. See AGENTS.md → Video uploads.
	const {
		upload,
		uploading: isUploading,
		error: hadUploadError,
		videoUploadState: chunkedState,
		abortVideoUpload: abortChunkedUpload
	} = useAzureFileUpload((uuid) => {
		// `setUUID` callback fires when the chunked /complete returns.
		// processingStatus = "Processing" because Bitmovin encoding only
		// starts at completion; the poll loop below flips to
		// "ReadyForPlayback" once the manifest is in blob storage.
		const assetId = typeof uuid === "string" ? uuid : null;
		if (!assetId) return;
		onUpdateRef.current({
			...blockRef.current,
			file: null,
			content: assetId,
			processingStatus: "Processing"
		});
	});

	const uploadError = hadUploadError
		? (chunkedState?.error ?? "Upload failed")
		: null;

	// Polls the asset endpoint while the block has a persisted reference.
	// `useAzureVideoDownload` accepts a falsy uuid and no-ops, so it's
	// safe to always call (Rules of Hooks). When `url` resolves, the
	// video is ready for playback — propagate that to the form so the
	// Send button can enable.
	//
	// Note: we poll on `block.content` directly rather than the gated
	// `hasPersistedRef`. The latter requires `!block.file`, which is
	// true for both fresh post-upload blocks (file cleared by the
	// setUUID callback) and reopened drafts — but without the gate we
	// can also start polling for in-session uploads that have a
	// previewUrl AND a content reference, which is what we need to drive
	// the "still encoding" overlay below.
	const persistedRef = block.content || null;
	const { url: persistedUrl, processingStatus: persistedStatus } =
		useAzureVideoDownload("video", undefined, persistedRef);

	useEffect(() => {
		if (!persistedRef) return;
		const next = persistedUrl ? "ReadyForPlayback" : "Processing";
		if (blockRef.current.processingStatus !== next) {
			onUpdateRef.current({ ...blockRef.current, processingStatus: next });
		}
	}, [persistedRef, persistedUrl]);

	// Drive the encoding overlay shown alongside the local preview.
	// Without this, once chunked upload completes we'd render the local
	// blob with no indication that Bitmovin is still encoding — authors
	// see a playable preview and assume the whole pipeline is finished.
	//
	// We derive the flag from `persistedUrl` (stable until Bitmovin
	// finishes) rather than `persistedStatus` — `useAzureVideoDownload`
	// clears `processingStatus` back to "" every 4s between polls (see
	// useAzureVideoDownload.tsx), which would otherwise make the
	// overlay flicker.
	const isEncoding = !isUploading && !!persistedRef && !persistedUrl;
	const encodingState = useVideoEncodingProgress(persistedRef, isEncoding);

	useEffect(() => {
		setError(uploadError ? "Failed to upload video, try again." : null);
		if (uploadError && blockRef.current.file) {
			onUpdateRef.current({ ...blockRef.current, file: null });
		}
	}, [uploadError]);

	useEffect(() => {
		const url = block.previewUrl;
		if (url) {
			return () => URL.revokeObjectURL(url);
		}
	}, [block.previewUrl]);

	const handleFile = (file: File) => {
		if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
			setError("Only MP4 and QuickTime files are allowed.");
			return;
		}
		if (file.size > MAX_VIDEO_SIZE) {
			setError(`Video must be under ${formatBytes(MAX_VIDEO_SIZE)}.`);
			return;
		}
		setError(null);
		const url = URL.createObjectURL(file);
		onUpdate({ ...block, file, previewUrl: url, content: "" });
		upload(file, "video");
	};

	const handleCancelUpload = async () => {
		await abortChunkedUpload();
		onUpdate({ ...block, file: null, previewUrl: null, content: "" });
	};

	const handleRetryUpload = () => {
		if (block.file) {
			setError(null);
			upload(block.file, "video");
		}
	};

	return (
		<div>
			<input
				ref={fileInputRef}
				type="file"
				accept="video/mp4,video/quicktime"
				className="hidden"
				onChange={(e) => {
					const file = e.target.files?.[0];
					if (file) handleFile(file);
				}}
			/>
			{hasPreview || hasPersistedRef ? (
				<>
					<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle relative">
						{hasPreview ? (
							<video
								controls
								src={block.previewUrl!}
								aria-label="Announcement video preview"
								className="w-full max-h-[400px]"
							/> // eslint-disable-line
						) : (
							<PersistedVideoPreview
								reference={block.content}
								url={persistedUrl}
								processingStatus={persistedStatus}
							/>
						)}
						<VideoUploadProgress
							state={isUploading ? chunkedState : null}
							onCancel={handleCancelUpload}
							variant="overlay"
						/>
						{/* Keep the encoding indicator on top of the local
						    preview until Bitmovin finishes — without it,
						    authors see a playable blob and assume the
						    pipeline is done while the backend is still
						    encoding. Mirrors the overlay pattern in
						    FileUploadStep / Carousel VideoUpload. */}
						{hasPreview && isEncoding && (
							<VideoProcessingProgress
								state={encodingState}
								variant="overlay"
							/>
						)}
					</div>
					<div className="flex items-center gap-2">
						<button
							type="button"
							disabled={isUploading}
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors disabled:opacity-50 disabled:pointer-events-none"
							onClick={() => fileInputRef.current?.click()}
						>
							Change video
						</button>
						<button
							type="button"
							disabled={isUploading}
							className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-destructive/40 bg-white text-destructive text-sm font-medium cursor-pointer hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors disabled:opacity-50 disabled:pointer-events-none"
							onClick={() =>
								onUpdate({
									...block,
									file: null,
									previewUrl: null,
									content: ""
								})
							}
						>
							Remove
						</button>
						{uploadError && block.file && (
							<button
								type="button"
								onClick={handleRetryUpload}
								className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors"
							>
								Retry upload
							</button>
						)}
					</div>
				</>
			) : (
				<div
					role="button"
					tabIndex={0}
					onClick={() => fileInputRef.current?.click()}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							fileInputRef.current?.click();
						}
					}}
					onDragOver={(e) => {
						e.preventDefault();
						setIsDragging(true);
					}}
					onDragLeave={(e) => {
						e.preventDefault();
						setIsDragging(false);
					}}
					onDrop={(e) => {
						e.preventDefault();
						setIsDragging(false);
						const file = e.dataTransfer.files[0];
						if (file) handleFile(file);
					}}
					className={[
						"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors cursor-pointer",
						isDragging
							? "border-primary bg-primary/10"
							: "border-border bg-white hover:border-primary hover:bg-primary/5"
					].join(" ")}
				>
					<Video className="h-5 w-5 text-muted-foreground" />
					<div>
						<p className="text-sm font-medium text-foreground">
							Drag video here or click to browse
						</p>
						<p className="text-xs text-muted-foreground mt-1">
							MP4 or QuickTime, up to 50 GB
						</p>
					</div>
				</div>
			)}
			{error && (
				<p role="alert" className="text-sm text-destructive mt-2">
					{error}
				</p>
			)}
		</div>
	);
};

const blockTypeLabels: Record<ContentBlock["type"], string> = {
	text: "Text",
	image: "Image",
	video: "Video"
};

const blockTypeIcons: Record<
	ContentBlock["type"],
	FC<{ className?: string }>
> = {
	text: Type,
	image: ImagePlus,
	video: Video
};

const ContentBlocksEditor: FC<Props> = ({ blocks, onChange }) => {
	const [showAddMenu, setShowAddMenu] = useState(false);
	const addMenuRef = useRef<HTMLDivElement>(null);

	// Close the "Add content block" popover when the user clicks
	// outside it or presses Escape. Without this the menu stays open
	// indefinitely once triggered, which looked like a stuck UI.
	useEffect(() => {
		if (!showAddMenu) return;

		const handleOutsideClick = (e: globalThis.MouseEvent) => {
			if (
				addMenuRef.current &&
				!addMenuRef.current.contains(e.target as Node)
			) {
				setShowAddMenu(false);
			}
		};
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") setShowAddMenu(false);
		};

		document.addEventListener("mousedown", handleOutsideClick);
		document.addEventListener("keydown", handleEscape);
		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
			document.removeEventListener("keydown", handleEscape);
		};
	}, [showAddMenu]);

	// All block mutators work by id rather than index. Upload callbacks
	// inside ImageBlock / VideoBlock close over the block they were
	// fired from; identifying by id means that if the user reorders or
	// removes a different block while an upload is in flight, the
	// callback still patches the right block (or no-ops if the block is
	// gone) — whereas capturing an index would silently overwrite
	// whatever block now sits at that position.
	const updateBlock = (id: string, updated: ContentBlock) => {
		onChange(blocks.map((b) => (b.id === id ? updated : b)));
	};

	const removeBlock = (id: string) => {
		onChange(blocks.filter((b) => b.id !== id));
	};

	const moveBlock = (id: string, direction: "up" | "down") => {
		const index = blocks.findIndex((b) => b.id === id);
		if (index === -1) return;
		const target = direction === "up" ? index - 1 : index + 1;
		if (target < 0 || target >= blocks.length) return;
		const next = [...blocks];
		[next[index], next[target]] = [next[target], next[index]];
		onChange(next);
	};

	// A block is in-flight when a File was picked but no persisted
	// reference has landed yet. We use this to disable reorder/remove
	// on the uploading block (second defence after the id-based
	// mutators), and the parent form reads it via `blocks` to keep
	// Save/Send disabled until everything has uploaded.
	const isBlockUploading = (block: ContentBlock) =>
		(block.type === "image" || block.type === "video") &&
		Boolean(block.file) &&
		!block.content;

	const addBlock = (type: ContentBlock["type"]) => {
		const newBlock: ContentBlock = {
			id: generateId(),
			type,
			content: "",
			file: null,
			previewUrl: null
		};
		onChange([...blocks, newBlock]);
		setShowAddMenu(false);
	};

	return (
		<div className="flex flex-col gap-3">
			{blocks.map((block, index) => {
				const Icon = blockTypeIcons[block.type];
				const uploading = isBlockUploading(block);
				return (
					<div
						key={block.id}
						className="rounded-lg border border-border bg-white"
					>
						{/* Block header */}
						<div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-subtle rounded-t-lg">
							{/* Decorative icon — no drag handler is wired up yet, so
							    hide from assistive tech to avoid implying a function
							    that doesn't exist. */}
							<GripVertical
								aria-hidden="true"
								className="h-4 w-4 text-zinc-300"
							/>
							<Icon
								aria-hidden="true"
								className="h-4 w-4 text-muted-foreground"
							/>
							<span className="text-sm font-medium text-muted-foreground">
								{blockTypeLabels[block.type]}
							</span>

							<div className="ml-auto flex items-center gap-1">
								<button
									type="button"
									onClick={() => moveBlock(block.id, "up")}
									disabled={index === 0 || uploading}
									aria-label="Move block up"
									className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:pointer-events-none transition-colors"
									title="Move up"
								>
									<ChevronUp className="h-4 w-4 text-muted-foreground" />
								</button>
								<button
									type="button"
									onClick={() => moveBlock(block.id, "down")}
									disabled={index === blocks.length - 1 || uploading}
									aria-label="Move block down"
									className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:pointer-events-none transition-colors"
									title="Move down"
								>
									<ChevronDown className="h-4 w-4 text-muted-foreground" />
								</button>
								<button
									type="button"
									onClick={() => removeBlock(block.id)}
									disabled={uploading}
									aria-label="Remove block"
									className="p-1 rounded hover:bg-muted disabled:opacity-30 disabled:pointer-events-none transition-colors"
									title="Remove block"
								>
									<Trash2 className="h-4 w-4 text-destructive" />
								</button>
							</div>
						</div>

						{/* Block content */}
						<div className="p-3">
							{block.type === "text" && (
								<RichTextEditor
									content={block.content}
									onChange={(html) =>
										updateBlock(block.id, {
											...block,
											content: html
										})
									}
									placeholder="Write content..."
								/>
							)}
							{block.type === "image" && (
								<ImageBlock
									block={block}
									onUpdate={(updated) => updateBlock(block.id, updated)}
								/>
							)}
							{block.type === "video" && (
								<VideoBlock
									block={block}
									onUpdate={(updated) => updateBlock(block.id, updated)}
								/>
							)}
						</div>
					</div>
				);
			})}

			{/* Add block button */}
			<div className="relative" ref={addMenuRef}>
				<button
					type="button"
					onClick={() => setShowAddMenu(!showAddMenu)}
					aria-haspopup="menu"
					aria-expanded={showAddMenu}
					className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-border bg-white text-sm font-medium text-muted-foreground hover:border-primary hover:text-primary transition-colors"
				>
					<Plus className="h-4 w-4" />
					Add content block
				</button>

				{showAddMenu && (
					// No role="menu" here — we haven't implemented the arrow-key
					// navigation and menuitem semantics a proper ARIA menu needs.
					// The three plain buttons below have native button roles, which
					// is the right thing for a simple popover list of actions.
					<div className="absolute top-full left-0 mt-1 z-10 bg-white border border-border rounded-lg shadow-lg py-1 w-48">
						<button
							type="button"
							onClick={() => addBlock("text")}
							className="flex items-center gap-2.5 w-full px-4 py-2 text-sm hover:bg-muted text-left"
						>
							<Type className="h-4 w-4 text-muted-foreground" />
							<span className="text-foreground">Text</span>
						</button>
						<button
							type="button"
							onClick={() => addBlock("image")}
							className="flex items-center gap-2.5 w-full px-4 py-2 text-sm hover:bg-muted text-left"
						>
							<ImagePlus className="h-4 w-4 text-muted-foreground" />
							<span className="text-foreground">Image</span>
						</button>
						<button
							type="button"
							onClick={() => addBlock("video")}
							className="flex items-center gap-2.5 w-full px-4 py-2 text-sm hover:bg-muted text-left"
						>
							<Video className="h-4 w-4 text-muted-foreground" />
							<span className="text-foreground">Video</span>
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

export default ContentBlocksEditor;
